import { createFileRoute } from '@tanstack/react-router'
import { Section, SectionHeader } from '@/components/ui/section'
import { Button } from '@/components/ui/button'
import {
  Database,
  Monitor,
  Globe,
  Server,
  Download,
  ExternalLink
} from 'lucide-react'

export const Route = createFileRoute('/resources')({
  component: ResourcesComponent,
})

function ResourcesComponent() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero */}
      <section className="py-20 px-4 text-center border-b border-white/10 bg-background">
        <h1 className="text-4xl font-bold mb-4">Trading Resources</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Essential tools, data providers, and platforms for the professional algorithmic trader.
        </p>
      </section>

      {/* Platforms */}
      <Section title="Trading Platforms">
        <SectionHeader title="Execution & Charting" description="Compatible platforms for our algorithms." />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          <ResourceCard
            icon={<Monitor className="h-6 w-6 text-blue-400" />}
            title="NinjaTrader 8"
            description="Industry standard for futures. C# based automation with robust backtesting."
            tags={['Execution', 'Strategy']}
            link="#"
          />
          <ResourceCard
            icon={<ActivityIcon className="h-6 w-6 text-orange-400" />}
            title="Sierra Chart"
            description="Ultra-low latency, highly customizable, C++ based platform for power users."
            tags={['Performance', 'Dom']}
            link="#"
          />
          <ResourceCard
            icon={<Globe className="h-6 w-6 text-green-400" />}
            title="QuantTower"
            description="Modern multi-asset platform with C# API and DOM surface features."
            tags={['Modern', 'Multi-Asset']}
            link="#"
          />
        </div>
      </Section>

      {/* Data Feeds */}
      <Section className="bg-white/5 border-y border-white/10">
        <SectionHeader title="Data Feeds" description="High-fidelity market data providers." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          <ResourceCard
            icon={<Server className="h-6 w-6 text-purple-400" />}
            title="Rithmic"
            description="Unfiltered tick data. The gold standard for order flow analysis."
            tags={['MBO', 'Low Latency']}
            link="#"
          />
          <ResourceCard
            icon={<Database className="h-6 w-6 text-blue-400" />}
            title="CQG"
            description="Reliable, global coverage for professional desks."
            tags={['Global', 'Reliable']}
            link="#"
          />
          <ResourceCard
            icon={<Database className="h-6 w-6 text-yellow-400" />}
            title="dxFeed"
            description="Institutional grade data for equities and futures."
            tags={['Institutional']}
            link="#"
          />
          <ResourceCard
            icon={<Database className="h-6 w-6 text-cyan-400" />}
            title="Kinetick"
            description="Optimized for NinjaTrader. Reliable historic data."
            tags={['EOD', 'Intraday']}
            link="#"
          />
        </div>
      </Section>

      {/* Glossary */}
      <Section title="Glossary">
        <SectionHeader title="Key Terminology" description="Speak the language of the market." />
        <div className="grid md:grid-cols-2 gap-4 mt-8">
          <GlossaryItem term="CVD (Cumulative Volume Delta)" definition="The cumulative sum of the difference between buying and selling volume. Used to identify aggressive market participants." />
          <GlossaryItem term="POC (Point of Control)" definition="The price level with the highest traded volume for a specific session or timeframe." />
          <GlossaryItem term="DOM (Depth of Market)" definition="A visual display of pending buy and sell orders at various price levels." />
          <GlossaryItem term="Iceberg Order" definition="A large order divided into smaller visible portions to hide the total quantity from other market participants." />
          <GlossaryItem term="VWAP (Volume Weighted Average Price)" definition="The average price a security has traded at throughout the day, based on both volume and price." />
          <GlossaryItem term="MBO (Market By Order)" definition="Data feed that provides full visibility of individual orders in the queue, not just price levels." />
        </div>
      </Section>
    </div>
  )
}

function ResourceCard({ icon, title, description, tags, link }: { icon: React.ReactNode, title: string, description: string, tags: string[], link: string }) {
  return (
    <div className="flex flex-col p-6 rounded-xl border border-white/10 bg-background hover:bg-white/5 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="p-2 rounded-lg bg-white/5 border border-white/5">{icon}</div>
        <a href={link} className="text-muted-foreground hover:text-white"><ExternalLink className="h-4 w-4" /></a>
      </div>
      <h3 className="font-bold text-lg mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground mb-4 flex-grow">{description}</p>
      <div className="flex gap-2 flex-wrap">
        {tags.map(tag => (
          <span key={tag} className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10 text-gray-400">
            {tag}
          </span>
        ))}
      </div>
    </div>
  )
}

function GlossaryItem({ term, definition }: { term: string, definition: string }) {
  return (
    <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
      <h4 className="font-bold text-blue-400 mb-1">{term}</h4>
      <p className="text-sm text-muted-foreground">{definition}</p>
    </div>
  )
}

// Simple Activity Icon substitute
function ActivityIcon(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  )
}
